import { makeAutoObservable, reaction, runInAction } from 'mobx'
import escapeRegExp from 'lodash/escapeRegExp'
import { RootStore } from 'stores'
import {
  createTrainingComponent,
  getTrainingComponentById,
  instantiateTrainingComponent,
  listTrainingComponents
} from 'API/training-component'
import { getAllProcesses, getDetailProcessById } from 'API/process'
import { getCollectionsByFilterWithRelations } from 'API/collection'
import { ICollection } from 'interfaces/collection'
import { IProcessWithRelations } from 'interfaces/process'
import { LibraryItem, LibraryItemType } from 'interfaces/practice-component'
import { TrainingComponentType } from 'interfaces/training-component'
import { LibrarySection, MutationResult, SearchState, SectionState } from './practiceLibraryStore'
import { mapTrainingComponentToLibraryItem } from 'utils/training/map-component-to-library-item'
import { flushPendingNodeEdits, resolveSavedNodeName } from 'utils/plan-builder-pending-edits'
import {
  mapCollectionToLibraryItem,
  mapProcessToLibraryItem
} from 'utils/practice/map-search-sources-to-library-item'

const PAGE_SIZE = 10
// Cap per-source search results; sources are merged so this bounds the list.
const SEARCH_LIMIT = 20

// The component (TrainingComponent) library types, vs the stack/collection sources.
// Training has no rotation concept, unlike Practice's period/lane/block/station/rotation.
export const COMPONENT_LIBRARY_TYPES: TrainingComponentType[] = ['week', 'day', 'block', 'station']

export function isTrainingComponentLibraryType(type: LibraryItemType): boolean {
  return (COMPONENT_LIBRARY_TYPES as string[]).includes(type)
}

const byTitleAsc = (a: LibraryItem, b: LibraryItem): number => a.title.localeCompare(b.title)

function emptySection(): SectionState {
  return { items: [], skip: 0, hasMore: true, loading: false, loaded: false, error: false }
}

/**
 * Store for the reusable Training Component library (BuilderLibrary panel).
 * Mirrors `PracticeLibraryStore` 1:1 (sections, detail cache, search, mutations,
 * save/instantiate tokens, org-switch reset) — retargeted to the
 * `training-component` endpoints and the week/day/block/station type union
 * (no rotation). `LibrarySection`/`SectionState`/`SearchState`/`MutationResult`
 * are reused directly from `practiceLibraryStore` since they carry no
 * Practice-specific shape (DRY).
 */
export default class TrainingLibraryStore {
  rootStore: RootStore

  mostUsed: SectionState = emptySection()
  recent: SectionState = emptySection()

  // Detail payloads (GET /{id}) cached by component id to avoid refetch on re-open.
  detailCache: Map<number, LibraryItem> = new Map()

  // Merged search results (components → stacks → collections). Owns its own data
  // + generation token so a fast typist can't apply a stale response, and so it
  // never reads the shared processStore/collectionStore observables.
  searchResults: SearchState = { items: [], loading: false, error: false, query: '' }
  private searchGen = 0
  // Raw stack (Process) objects from the last search, keyed by id — the drill-down
  // reads their embedded `.steps` without a second fetch.
  searchStackCache: Map<number, IProcessWithRelations> = new Map()
  // Raw Collection objects from the last search, keyed by id — the collection
  // drill-down reads their embedded `collectionsProcesses[].process(.steps)`
  // (filter-with-relations already returns names + drills with real ids).
  searchCollectionCache: Map<number, ICollection> = new Map()

  // Set when the API returns 403 — the caller is not a coach. UI uses this to
  // gate mutating affordances rather than surfacing raw errors.
  isForbidden = false

  // Tokens (`nodeType:id@contentSignature`) of nodes already saved as components
  // this session. SHARED across every save entry point (inspector footer + group
  // bookmark + kebab) so they show one "already saved" state for the same node.
  // A content edit changes the signature → new token → buttons re-enable.
  savedComponentTokens: Set<string> = new Set()

  // Tokens whose save is currently in flight — SHARED so every save button for the
  // same node shows the spinner together, no matter which one was clicked.
  savingComponentTokens: Set<string> = new Set()

  markComponentSaved(token: string): void {
    const next = new Set(this.savedComponentTokens)
    next.add(token)
    this.savedComponentTokens = next
  }

  isComponentSaved(token: string): boolean {
    return this.savedComponentTokens.has(token)
  }

  startComponentSaving(token: string): void {
    const next = new Set(this.savingComponentTokens)
    next.add(token)
    this.savingComponentTokens = next
  }

  endComponentSaving(token: string): void {
    const next = new Set(this.savingComponentTokens)
    next.delete(token)
    this.savingComponentTokens = next
  }

  isComponentSaving(token: string): boolean {
    return this.savingComponentTokens.has(token)
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    // rootStore excluded — it's the whole store tree, must not be made observable.
    makeAutoObservable(this, { rootStore: false })

    // Invalidate cached library data when the active org/team changes so the
    // builder refetches for the new scope. Library data is org-scoped
    // server-side; the singleton store would otherwise keep the prior scope's
    // sections across an in-app team switch (no full reload). App-lifetime
    // singleton → the reaction never needs disposal.
    reaction(
      () => this.rootStore.authStore.activeOrgId,
      () => this.resetSections()
    )
  }

  private sectionState(section: LibrarySection): SectionState {
    return section === 'mostUsed' ? this.mostUsed : this.recent
  }

  fetchMostUsed(reset = false): Promise<void> {
    return this.fetchSection('mostUsed', reset)
  }

  fetchRecent(reset = false): Promise<void> {
    return this.fetchSection('recent', reset)
  }

  /** Load the next page of a section (skip += PAGE_SIZE) for infinite scroll. */
  fetchMore(section: LibrarySection): Promise<void> {
    return this.fetchSection(section, false)
  }

  /** Reset + refetch a section from the first page (used by the error-retry). */
  reload(section: LibrarySection): Promise<void> {
    return this.fetchSection(section, true)
  }

  private async fetchSection(section: LibrarySection, reset: boolean): Promise<void> {
    const state = this.sectionState(section)
    // Dedupe concurrent loads (double sentinel hits during infinite scroll).
    if (state.loading) return
    if (reset) {
      state.items = []
      state.skip = 0
      state.hasMore = true
      state.loaded = false
      state.error = false
    }
    if (!state.hasMore && state.loaded) return

    state.loading = true
    state.error = false
    try {
      const { items, total } = await listTrainingComponents({
        sort: section,
        skip: state.skip,
        limit: PAGE_SIZE
      })
      const mapped = items.map(mapTrainingComponentToLibraryItem)
      runInAction(() => {
        const merged = reset ? mapped : [...state.items, ...mapped]
        state.items = merged
        state.skip = merged.length
        // Stop paging when the last page was short OR we reached the reported
        // total — short-page is the authoritative signal (guards a stale/inflated
        // `total` from driving an endless request loop).
        state.hasMore = mapped.length === PAGE_SIZE && merged.length < total
        state.loaded = true
        state.loading = false
      })
    } catch (error) {
      runInAction(() => {
        state.loading = false
        state.error = true
        // Halt scroll/auto-fill retries; loaded stays false so a remount
        // auto-retries, and reload() re-enables paging explicitly.
        state.hasMore = false
        if ((error as { response?: { status?: number } })?.response?.status === 403) {
          this.isForbidden = true
        }
      })
    }
  }

  /** Fetch (and cache) the detail payload for a component so the preview can
   *  render its children — list DTOs exclude payload. */
  async fetchDetail(id: number): Promise<LibraryItem | undefined> {
    const cached = this.detailCache.get(id)
    if (cached?.detail) return cached
    try {
      const dto = await getTrainingComponentById(id)
      const item = mapTrainingComponentToLibraryItem(dto)
      runInAction(() => {
        this.detailCache.set(id, item)
      })
      return item
    } catch (error) {
      if ((error as { response?: { status?: number } })?.response?.status === 403) {
        runInAction(() => {
          this.isForbidden = true
        })
      }
      return undefined
    }
  }

  /**
   * Fetch a stack's (Process) full drill detail on demand. The search response
   * only carries lightweight steps (for the card's drill count), so opening the
   * drill list issues a fresh /processes/{id}/detail request for complete drill
   * data. The result is cached back into the search-stack map for reuse.
   */
  async fetchStackDetail(id: number): Promise<IProcessWithRelations | undefined> {
    try {
      const process = await getDetailProcessById(id)
      // The /detail endpoint is a raw Mongo aggregation that does not map nested
      // step `_id` → `id` (unlike the LoopBack list endpoint the search uses).
      // Backfill `id` so the drill list's add-to-station and drag paths read a
      // real stepId instead of `NaN`/null (which the API rejects as 422).
      const normalized: IProcessWithRelations = {
        ...process,
        steps: Array.isArray(process.steps)
          ? process.steps.map(step => ({ ...step, id: step.id ?? step._id }))
          : process.steps
      }
      runInAction(() => {
        this.searchStackCache.set(id, normalized)
      })
      return normalized
    } catch {
      return undefined
    }
  }

  // ─── Search (components + stacks + collections) ───────────────────────────

  /**
   * Merged search across the three library sources, ordered components → stacks
   * → collections. `typeFilter` scopes to one source/component-type (chip). A
   * generation token drops out-of-order responses so a fast typist never sees
   * stale results. Sources are queried via APIs that return data directly — the
   * shared process/collection stores are never read (avoids race + filter leak).
   */
  async search(query: string, typeFilter?: LibraryItemType | null): Promise<void> {
    const q = query.trim()
    if (!q) {
      this.clearSearch()
      return
    }

    const gen = ++this.searchGen
    // Bound the raw-source caches to the current search's results (drill-down reads them right after).
    this.searchStackCache.clear()
    this.searchCollectionCache.clear()
    this.searchResults.loading = true
    this.searchResults.error = false
    this.searchResults.query = q

    const includeComponents = !typeFilter || isTrainingComponentLibraryType(typeFilter)
    const componentType =
      typeFilter && isTrainingComponentLibraryType(typeFilter) ? (typeFilter as TrainingComponentType) : undefined
    const includeStack = !typeFilter || typeFilter === 'stack'
    const includeCollection = !typeFilter || typeFilter === 'collection'

    // Independent sources — one failing (e.g. a flaky /processes) must not blank
    // the others, so settle all and render whatever resolved (order preserved:
    // components → stacks → collections). Error only when every source fails.
    const settled = await Promise.allSettled([
      includeComponents ? this.searchComponents(q, componentType) : Promise.resolve([] as LibraryItem[]),
      includeStack ? this.searchStacks(q) : Promise.resolve([] as LibraryItem[]),
      includeCollection ? this.searchCollections(q) : Promise.resolve([] as LibraryItem[])
    ])
    if (gen !== this.searchGen) return // a newer search superseded this one

    const items: LibraryItem[] = []
    settled.forEach(result => {
      if (result.status === 'fulfilled') items.push(...result.value)
    })
    const allFailed = settled.every(result => result.status === 'rejected')
    const forbidden = settled.some(
      result =>
        result.status === 'rejected' &&
        (result.reason as { response?: { status?: number } })?.response?.status === 403
    )

    runInAction(() => {
      this.searchResults.items = items
      this.searchResults.loading = false
      this.searchResults.error = allFailed
      if (forbidden) this.isForbidden = true
    })
  }

  /** Cancel any in-flight search and clear results (empty query). */
  clearSearch(): void {
    this.searchGen++
    this.searchResults = { items: [], loading: false, error: false, query: '' }
  }

  /** Raw stack (Process) from the last search — the drill-down reads its embedded `.steps`. */
  getSearchStack(id: number): IProcessWithRelations | undefined {
    return this.searchStackCache.get(id)
  }

  private async searchComponents(q: string, type?: TrainingComponentType): Promise<LibraryItem[]> {
    const { items } = await listTrainingComponents({ q, type, sort: 'recent', limit: SEARCH_LIMIT })
    return items.map(mapTrainingComponentToLibraryItem).sort(byTitleAsc)
  }

  private async searchStacks(q: string): Promise<LibraryItem[]> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const keyword = escapeRegExp(q)
    const filter = {
      offset: 0,
      limit: SEARCH_LIMIT,
      where: {
        isPublished: true,
        organizationId,
        name: { like: `.*${keyword}`, options: 'i' },
        $or: [{ archivedAt: { $exists: false } }, { archivedAt: { $eq: null } }]
      },
      order: ['updatedAt DESC'],
      include: [{ relation: 'steps' }, { relation: 'tags' }]
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processes = await getAllProcesses(filter as any)
    runInAction(() => {
      processes.forEach(p => this.searchStackCache.set(p.id, p))
    })
    return processes.map(mapProcessToLibraryItem).sort(byTitleAsc)
  }

  private async searchCollections(q: string): Promise<LibraryItem[]> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const userId = this.rootStore.authStore.userDetail?.id
    const result = await getCollectionsByFilterWithRelations({
      name: q,
      organizationId,
      userId,
      isPublished: true,
      skip: 0,
      limit: SEARCH_LIMIT
    })
    const collections = result.paginatedResults ?? []
    runInAction(() => {
      collections.forEach(c => this.searchCollectionCache.set(c.id, c))
    })
    return collections.map(mapCollectionToLibraryItem).sort(byTitleAsc)
  }

  /** Raw collection from the last search — the drill-down reads its child stacks. */
  getSearchCollection(id: number): ICollection | undefined {
    return this.searchCollectionCache.get(id)
  }

  // ─── Mutations (coach-only server-side) ───────────────────────────────────

  /**
   * Deep-clone a component into the plan under the given parent. Returns true on
   * success. A 403 (non-coach) sets isForbidden so the UI can gate affordances;
   * callers own success/error toasts and the plan-tree reload.
   */
  async instantiate(
    componentId: number,
    parentType: 'trainingplan' | TrainingComponentType,
    parentId: number,
    position: number
  ): Promise<MutationResult> {
    try {
      await instantiateTrainingComponent(componentId, { parentType, parentId, position })
      // usageCount + lastUsedAt bumped server-side — refresh both sections.
      this.refreshSections()
      return { ok: true, forbidden: false }
    } catch (error) {
      return this.toMutationError(error)
    }
  }

  /** In-flight save-as-component requests keyed by `${componentType}:${rootId}`.
   *  Request bookkeeping only — no UI reads it, so nothing reacts to it. */
  private inFlightSaves = new Map<string, Promise<MutationResult>>()

  /**
   * Save a live plan subtree as a reusable component.
   *
   * Deduped per node while in flight: the flush below refetches the plan, which
   * changes the content signature the save buttons key their disabled state on,
   * so a second click can slip past them mid-save and create a duplicate entry.
   * Node identity survives a refetch, so it is what the guard keys on; the second
   * caller simply rides the first request's result.
   */
  async saveAsComponent(
    type: TrainingComponentType,
    rootId: number,
    name: string,
    token?: string
  ): Promise<MutationResult> {
    const nodeKey = `${type}:${rootId}`
    const inFlight = this.inFlightSaves.get(nodeKey)
    if (inFlight) return inFlight

    const run = this.runSaveAsComponent(type, rootId, name, token)
    this.inFlightSaves.set(nodeKey, run)
    try {
      return await run
    } finally {
      this.inFlightSaves.delete(nodeKey)
    }
  }

  private async runSaveAsComponent(
    type: TrainingComponentType,
    rootId: number,
    name: string,
    token?: string
  ): Promise<MutationResult> {
    // Mark in-flight so every save button for this node shows the spinner together.
    if (token) this.startComponentSaving(token)
    try {
      // The server snapshots the subtree from the database, so any inspector edit
      // still sitting in the auto-save debounce — or still on the wire — must land
      // first, otherwise a name or note typed a moment ago is missing from the
      // saved component. Flushing here (rather than in each button) covers every
      // save entry point.
      const flushed = await flushPendingNodeEdits()
      await createTrainingComponent({ type, rootId, name: resolveSavedNodeName(flushed, type, rootId, name) })
      // Mark this node's content-token as saved so every save button for it shares
      // one disabled state until the node changes again.
      if (token) this.markComponentSaved(token)
      // The new component is stamped lastUsedAt at creation, so it appears at
      // the top of Recently Used right away — refresh both sections.
      this.refreshSections()
      return { ok: true, forbidden: false }
    } catch (error) {
      return this.toMutationError(error)
    } finally {
      if (token) this.endComponentSaving(token)
    }
  }

  /** Re-fetch both default sections from page one (after a library mutation). */
  private refreshSections(): void {
    void this.fetchMostUsed(true)
    void this.fetchRecent(true)
  }

  // Per-call result — callers check `forbidden` from this call, not the shared
  // (sticky) isForbidden flag, so a later non-403 error can't mis-report as 403.
  private toMutationError(error: unknown): MutationResult {
    const forbidden = (error as { response?: { status?: number } })?.response?.status === 403
    if (forbidden) {
      runInAction(() => {
        this.isForbidden = true
      })
    }
    return { ok: false, forbidden }
  }

  /** Reset all library state (used on team/org switch or leaving the builder). */
  resetSections(): void {
    this.mostUsed = emptySection()
    this.recent = emptySection()
    this.detailCache.clear()
    this.searchStackCache.clear()
    this.clearSearch()
    this.isForbidden = false
  }
}
